import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges, TemplateRef } from '@angular/core'
import { NavigationEnd, Router } from '@angular/router'
import { NavFavoritesService } from '@employer/app/services/nav-favorites.service'
import type { IEmployerNavigationItem } from '@employer/app/services/navigation.service'
import { INavigationItem, idForNavItem } from '@engineering11/ui-lib/e11-nav-left'
import { navTourAnchor } from '@employer/app/services/nav-tour-anchor'
import { Observable, distinctUntilChanged, filter, map, startWith } from 'rxjs'

/**
 * One row of the app-owned left-nav tree (recursive), styled to match the cnect-ux
 * reference sidebar (https://cnect.github.io/cnect-ux — .nav-link / .nav-link-child /
 * .nav-group / .nav-subgroup, values copied from its compiled stylesheet):
 *
 *   - Top-level rows: 65%-white text; hover lifts to 85% on an 8%-white wash.
 *   - An OPEN top-level group becomes a contained card: 1px 18%-white border +
 *     5%-white fill, header text goes full white.
 *   - Children: plain indented rows (no guide rail), 55%-white text; the indent
 *     aligns child labels under the parent label.
 *   - Nested sub-groups (Website / Personnel / Settings) are separated by a
 *     divider line above each (.nav-subgroup border-top).
 *   - Active row: 10%-white wash + white text + DemiBold. (This theme expresses
 *     weight via font-FAMILY — AvenirNext single-weight faces — so the active rule
 *     swaps family rather than font-weight.)
 *   - All icons render outlined (material-symbols-outlined).
 *
 * Behavior is unchanged from the approved contract:
 *   R1 group click toggles only that group; R2 leaf click navigates and ancestors
 *   stay open (`leafNavigated` bubbles so the drawer/flyout can close); R3 sub-groups
 *   behave identically; R5 expand state lives in the surface-owned `openState` map.
 */
@Component({
  selector: 'app-nav-item',
  template: `
    <ng-container *ngIf="!item.isVisible || (item.isVisible | async)">
      <!-- Custom-slot item (Messages) — the app supplies the template (R8) -->
      <ng-container *ngIf="item.customTemplateKey && customTemplates[item.customTemplateKey]">
        <ng-container *ngTemplateOutlet="customTemplates[item.customTemplateKey]"></ng-container>
      </ng-container>

      <ng-container *ngIf="!item.customTemplateKey">
        <!-- ===== Top-level rows ===== -->
        <ng-container *ngIf="level === 0">
          <!-- Leaf -->
          <a
            *ngIf="!hasChildren"
            [attr.id]="idFor(item)"
            [attr.data-tour-id]="tourAnchor(item)"
            class="nav-link e11-flex e11-items-center e11-gap-2 e11-h-12 e11-px-2 e11-mb-1 e11-cursor-pointer focus-visible:e11-outline focus-visible:e11-outline-2 focus-visible:e11-outline-skin-white/50"
            [routerLink]="item.route"
            [queryParams]="item.navigationExtras?.queryParams"
            routerLinkActive
            #rla="routerLinkActive"
            [routerLinkActiveOptions]="{ paths: 'exact', queryParams: 'ignored', fragment: 'ignored', matrixParams: 'ignored' }"
            [class.active]="item.isActive ? (activeOverride$ | async) : rla.isActive"
            (click)="onLeafClick()"
          >
            <span *ngIf="item.icon" class="material-symbols-outlined md-18">{{ item.icon }}</span>
            <span class="nav-label e11-flex-1 e11-truncate">{{ item.name }}</span>
            <ng-container *ngTemplateOutlet="favoriteStar"></ng-container>
          </a>

          <!-- Group: closed = plain row; open = contained card around header + children -->
          <div *ngIf="hasChildren" class="nav-group e11-mb-1" [ngClass]="{ active: isOpen }">
            <button
              type="button"
              [attr.id]="idFor(item)"
              [attr.data-tour-id]="tourAnchor(item)"
              class="nav-link nav-row-btn e11-flex e11-items-center e11-gap-2 e11-w-full e11-text-left e11-h-12 e11-px-2 e11-cursor-pointer e11-bg-transparent e11-border-0 focus-visible:e11-outline focus-visible:e11-outline-2 focus-visible:e11-outline-skin-white/50"
              [attr.aria-expanded]="isOpen"
              (click)="onGroupClick()"
            >
              <span *ngIf="item.icon" class="material-symbols-outlined md-18">{{ item.icon }}</span>
              <span class="nav-label e11-flex-1 e11-truncate">{{ item.name }}</span>
              <span class="material-symbols-outlined nav-chevron" [ngClass]="{ 'nav-chevron-open': isOpen }">expand_more</span>
            </button>
            <div class="nav-children" [ngClass]="{ 'nav-children-open': isOpen }">
              <div class="e11-pb-1.5 e11-flex e11-flex-col">
                <app-nav-item
                  *ngFor="let child of item.children"
                  [item]="child"
                  [level]="level + 1"
                  [openState]="openState"
                  [customTemplates]="customTemplates"
                  (leafNavigated)="leafNavigated.emit($event)"
                ></app-nav-item>
              </div>
            </div>
          </div>
        </ng-container>

        <!-- ===== Nested rows (any depth below top level) ===== -->
        <ng-container *ngIf="level > 0">
          <!-- Leaf child: plain indented row, label aligned under the parent label -->
          <a
            *ngIf="!hasChildren"
            [attr.id]="idFor(item)"
            [attr.data-tour-id]="tourAnchor(item)"
            class="nav-link-child e11-flex e11-items-center e11-gap-2 e11-h-10 e11-pl-[34px] e11-pr-2 e11-cursor-pointer focus-visible:e11-outline focus-visible:e11-outline-2 focus-visible:e11-outline-skin-white/50"
            [routerLink]="item.route"
            [queryParams]="item.navigationExtras?.queryParams"
            routerLinkActive
            #rla="routerLinkActive"
            [routerLinkActiveOptions]="{ paths: 'exact', queryParams: 'ignored', fragment: 'ignored', matrixParams: 'ignored' }"
            [class.active]="item.isActive ? (activeOverride$ | async) : rla.isActive"
            (click)="onLeafClick()"
          >
            <span *ngIf="item.icon" class="material-symbols-outlined md-18">{{ item.icon }}</span>
            <span class="nav-label e11-flex-1 e11-truncate">{{ item.name }}</span>
            <ng-container *ngTemplateOutlet="favoriteStar"></ng-container>
          </a>

          <!-- Sub-group: divider line above, icon + chevron header, children at the standard child indent -->
          <div *ngIf="hasChildren" class="nav-subgroup" [ngClass]="{ active: isOpen }">
            <button
              type="button"
              [attr.id]="idFor(item)"
              [attr.data-tour-id]="tourAnchor(item)"
              class="nav-link-child nav-row-btn e11-flex e11-items-center e11-gap-2 e11-w-full e11-text-left e11-h-10 e11-pl-[34px] e11-pr-2 e11-cursor-pointer e11-bg-transparent e11-border-0 focus-visible:e11-outline focus-visible:e11-outline-2 focus-visible:e11-outline-skin-white/50"
              [attr.aria-expanded]="isOpen"
              (click)="onGroupClick()"
            >
              <span *ngIf="item.icon" class="material-symbols-outlined md-18">{{ item.icon }}</span>
              <span class="nav-label e11-flex-1 e11-truncate">{{ item.name }}</span>
              <span class="material-symbols-outlined nav-chevron" [ngClass]="{ 'nav-chevron-open': isOpen }">expand_more</span>
            </button>
            <div class="nav-children" [ngClass]="{ 'nav-children-open': isOpen }">
              <div class="e11-flex e11-flex-col">
                <app-nav-item
                  *ngFor="let child of item.children"
                  [item]="child"
                  [level]="level + 1"
                  [openState]="openState"
                  [customTemplates]="customTemplates"
                  (leafNavigated)="leafNavigated.emit($event)"
                ></app-nav-item>
              </div>
            </div>
          </div>
        </ng-container>
      </ng-container>
    </ng-container>

    <!-- Pin/unpin star (CNCT-6764) — only on rows whose item carries a favoriteId -->
    <ng-template #favoriteStar>
      <ng-container *ngIf="favorite$ as fav$">
        <ng-container *ngIf="{ isFavorite: fav$ | async } as star">
          <button
            type="button"
            class="nav-star-btn e11-flex e11-items-center e11-justify-center e11-shrink-0 e11-cursor-pointer e11-bg-transparent e11-border-0 e11-p-0"
            [ngClass]="{ 'nav-star-active': star.isFavorite }"
            [attr.aria-label]="star.isFavorite ? 'Remove from favorites' : 'Add to favorites'"
            (click)="onStarClick($event)"
          >
            <span class="material-symbols-outlined md-18" [ngClass]="{ 'nav-star-filled': star.isFavorite }">star</span>
          </button>
        </ng-container>
      </ng-container>
    </ng-template>
  `,
  styles: [
    `
      /* Nav row label. Sized here rather than left to inherit the 14px body size, and scoped to
         .nav-label so the sibling icon and chevron spans, which are sized by their md-* utility,
         are untouched. */
      .nav-label {
        font-size: 15px;
      }

      /* Row + container look — values from the cnect-ux reference stylesheet. */
      .nav-link {
        color: rgba(255, 255, 255, 0.65);
        border-radius: 8px;
        transition: background 0.15s ease, color 0.15s ease;
      }
      .nav-link:hover {
        color: rgba(255, 255, 255, 0.85);
        background: rgba(255, 255, 255, 0.08);
      }
      .nav-link.active {
        color: #fff;
        background: rgba(255, 255, 255, 0.1);
      }
      .nav-link-child {
        color: rgba(255, 255, 255, 0.55);
        border-radius: 8px;
        transition: background 0.15s ease, color 0.15s ease;
      }
      .nav-link-child:hover {
        color: rgba(255, 255, 255, 0.8);
        background: rgba(255, 255, 255, 0.08);
      }
      .nav-link-child.active {
        color: #fff;
        background: rgba(255, 255, 255, 0.1);
      }
      /* Weight = font-family in this theme (single-weight AvenirNext faces). */
      .nav-link.active,
      .nav-link-child.active {
        font-weight: 500;
      }
      .nav-group {
        border: 1px solid transparent;
        border-radius: 8px;
        transition: border-color 0.15s ease, background 0.15s ease;
      }
      .nav-group.active {
        border-color: rgba(255, 255, 255, 0.18);
        background: rgba(255, 255, 255, 0.05);
      }
      .nav-group.active > .nav-link,
      .nav-subgroup.active > .nav-link-child {
        color: #fff;
      }
      /* Divider above each nested sub-group (Website / Personnel / Settings). */
      .nav-subgroup {
        border-top: 1px solid rgba(255, 255, 255, 0.18);
        margin-top: 4px;
        padding-top: 4px;
      }

      /* Buttons don't inherit typography by default — match the anchor rows exactly. */
      .nav-row-btn {
        font: inherit;
      }
      .nav-children {
        max-height: 0;
        overflow: hidden;
        padding-left: 4px;
        padding-right: 4px;
        transition: max-height padding-top padding-bottom 0.18s ease-in-out;
      }
      .nav-children.nav-children-open {
        max-height: 2000px;
        padding-top: 4px;
        padding-bottom: 4px;
      }
      .nav-chevron {
        font-size: 18px;
        color: rgba(255, 255, 255, 0.55);
        transition: transform 0.15s ease-in-out;
      }
      .nav-chevron.nav-chevron-open {
        transform: rotate(180deg);
      }

      /* Pin/unpin star — hidden until row hover/focus, always visible once favorited */
      .nav-star-btn {
        opacity: 0;
        color: rgba(255, 255, 255, 0.55);
        border-radius: 4px;
        transition: opacity 0.15s ease, color 0.15s ease;
      }
      .nav-link:hover .nav-star-btn,
      .nav-link-child:hover .nav-star-btn,
      .nav-star-btn:focus-visible,
      .nav-star-btn.nav-star-active {
        opacity: 1;
      }
      .nav-star-btn:hover {
        color: #fff;
      }
      .nav-star-filled {
        font-variation-settings: 'FILL' 1;
      }
      @media (prefers-reduced-motion: reduce) {
        .nav-children,
        .nav-chevron,
        .nav-link,
        .nav-link-child,
        .nav-group,
        .nav-star-btn {
          transition: none;
        }
      }
    `,
  ],
})
export class NavItemComponent implements OnChanges {
  @Input() item!: IEmployerNavigationItem
  @Input() level = 0
  @Input() openState!: Map<INavigationItem, boolean>
  @Input() customTemplates: Record<string, TemplateRef<unknown>> = {}
  @Output() leafNavigated = new EventEmitter<INavigationItem>()

  idFor = idForNavItem
  tourAnchor = navTourAnchor

  /** Rebuilt only on `item` change so the async pipe keeps one stable subscription */
  favorite$?: Observable<boolean>

  /**
   * Built only for rows that declare `isActive` — today just Company, so this is one
   * router subscription rather than one per row (CNCT-6779).
   */
  activeOverride$?: Observable<boolean>

  constructor(private navFavorites: NavFavoritesService, private router: Router) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['item']) {
      const favoriteId = this.item?.favoriteId
      this.favorite$ = favoriteId ? this.navFavorites.isFavorite$(favoriteId) : undefined

      const isActive = this.item?.isActive
      this.activeOverride$ = isActive
        ? this.router.events.pipe(
            filter((event): event is NavigationEnd => event instanceof NavigationEnd),
            map(event => event.urlAfterRedirects),
            startWith(this.router.url),
            map(url => isActive(url)),
            distinctUntilChanged()
          )
        : undefined
    }
  }

  get hasChildren(): boolean {
    return !!this.item.children?.length
  }

  get isOpen(): boolean {
    return this.openState.get(this.item) === true
  }

  onGroupClick(): void {
    this.item.onClick?.()
    this.openState.set(this.item, !this.isOpen)
  }

  onLeafClick(): void {
    this.item.onClick?.()
    this.leafNavigated.emit(this.item)
  }

  onStarClick(event: Event): void {
    event.preventDefault()
    event.stopPropagation()
    const favoriteId = this.item?.favoriteId
    if (favoriteId) this.navFavorites.toggleFavorite(favoriteId)
  }
}
